/**
 * A máscara nativa do compare — F-CP.12i.
 *
 * Desde a v25 o servidor compara UMA vez, na grade nativa do lado mais pobre,
 * num job com progresso, e guarda o resultado como pirâmide própria; o 100 %,
 * os zooms e a lista são reduções dela. Enquanto a máscara da identidade pedida
 * não existe, preview, tiles e lista respondem **202** com o estado do job, e
 * `GET …/mask-status` devolve o mesmo estado com 200. Este módulo é a tradução
 * desse contrato para a tela — que parâmetros formam a identidade, quando ela
 * está pronta, quando vale sondá-la, o que dizer enquanto não está — sem
 * React, sem `api.ts`, sem estado da página, para ser conferível por teste.
 *
 * A Área mínima e os pisos por classe NÃO entram na identidade da máscara
 * bruta (o piso é refeito sobre ela em segundos), mas VIAJAM na sondagem: o
 * `mask-status` do pedido garante o piso do pedido, não só a máscara bruta —
 * senão a sondagem diria «pronta» e o preview responderia 202 do piso
 * (revisão da 12i, F2). A Mínima, o modo, o corte sólido, o perdão, o filtro,
 * o giro e o alinhamento entram na identidade — cada mudança deles é um job
 * novo; trocar só a Área mínima é o passo do piso.
 */

export type CompareMaskState = "pending" | "running" | "ready" | "error";
export type CompareMaskStage = "raw" | "floor" | "regions" | "done";

export interface CompareMaskError {
  code: string;
  message?: string | null;
  retryable?: boolean;
}

/** O JSON de `mask-status` e do 202 — o mesmo nos dois. */
export interface CompareMaskStatus {
  state: CompareMaskState;
  stage?: CompareMaskStage | string | null;
  done?: number | null;
  total?: number | null;
  identity?: string | null;
  grid?: [number, number] | number[] | null;
  px_per_mm?: number | null;
  error?: CompareMaskError | null;
  retryable?: boolean;
  /** `channels` no PDF × PDF por separação: não há job, está sempre `ready`. */
  engine?: string | null;
}

/** O que identifica a máscara BRUTA, na régua da tela — mais o piso do pedido. */
export interface CompareMaskParams {
  offsetX: number;
  offsetY: number;
  scale: number;
  threshold: number;
  rotateMoving: number;
  /** O corte sólido já calculado (fator × limiar, teto 0,9); zero = desligado. */
  solidThreshold: number;
  mode: string;
  /** Só quando difere do gravado (`toleranceForRequest`). */
  registrationToleranceMm?: number;
  /** Só quando ligado (`noiseFilterForRequest`). */
  noiseFilter?: string;
  /** A Área mínima, só quando difere do padrão (`minAreaForRequest`): o piso do pedido. */
  minAreaMm2?: number;
}

/** Quanto esperar entre duas sondagens do estado enquanto o job roda. */
export const MASK_POLL_INTERVAL_MS = 1_000;

/**
 * Quantas sondagens seguidas podem falhar (rede, 5xx, 401 em renovação, 409
 * de cache) antes de a pílula parar de girar e oferecer «tentar de novo». Sem
 * teto, um backend fora deixava a pílula sem contagem e sem botão para sempre.
 */
export const MASK_POLL_MAX_FAILURES = 8;

/** As chaves da query que são do PISO, não da máscara bruta. */
const FLOOR_ONLY_KEYS = new Set(["min_area_mm2", "pixel_floor_text_mm2", "pixel_floor_image_mm2"]);

/**
 * A query de `mask-status` / `mask-retry` para estes parâmetros — a MESMA
 * regra de presença das URLs do preview e dos tiles: só o que difere do padrão
 * viaja, para o pedido de quem nunca mudou nada ser o mesmo de sempre.
 */
export function maskQueryFor(params: CompareMaskParams, physical: boolean): Record<string, string> {
  const query: Record<string, string> = {
    difference_threshold: String(params.threshold),
    scale: String(params.scale || 1),
  };
  if (physical) {
    query.offset_mm_x = String(params.offsetX);
    query.offset_mm_y = String(params.offsetY);
  } else {
    query.offset_x = String(Math.round(params.offsetX));
    query.offset_y = String(Math.round(params.offsetY));
  }
  if (params.rotateMoving) query.rotate_moving = String(params.rotateMoving);
  if (params.solidThreshold) query.solid_threshold = String(params.solidThreshold);
  if (params.mode && params.mode !== "appearance") query.mode = params.mode;
  if (params.registrationToleranceMm !== undefined) {
    query.registration_tolerance_mm = String(params.registrationToleranceMm);
  }
  if (params.noiseFilter !== undefined && params.noiseFilter !== "none") query.noise_filter = params.noiseFilter;
  if (params.minAreaMm2 !== undefined) query.min_area_mm2 = String(params.minAreaMm2);
  return query;
}

/** A chave de memoização da identidade: compare + query, estável para o mesmo pedido. */
export function maskIdentityKey(compareId: string | null | undefined, query: Record<string, string>): string | null {
  if (!compareId) return null;
  const partes = Object.keys(query)
    .sort()
    .map((chave) => `${chave}=${query[chave]}`);
  return `${compareId}?${partes.join("&")}`;
}

/**
 * As duas queries pedem a MESMA máscara bruta e diferem só no piso? Então a
 * identidade nova não volta ao «comparando do zero»: com a anterior pronta, o
 * que está por vir é o passo do piso (segundos), e é isso que a pílula diz
 * até a primeira sondagem responder.
 */
export function maskQueriesDifferOnlyByFloor(a: Record<string, string>, b: Record<string, string>): boolean {
  const chaves = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const chave of chaves) {
    if (FLOOR_ONLY_KEYS.has(chave)) continue;
    if (a[chave] !== b[chave]) return false;
  }
  return true;
}

/** Pronta para pedir preview, tiles e lista. `null` (ainda sem resposta) não é pronta. */
export function maskIsReady(status: CompareMaskStatus | null | undefined): boolean {
  return status?.state === "ready";
}

/** O job terminou — bem ou mal; não há mais o que sondar. */
export function maskIsSettled(status: CompareMaskStatus | null | undefined): boolean {
  return status?.state === "ready" || status?.state === "error";
}

/**
 * A sondagem de um par pode começar? (revisão da 12i, F1 e F5)
 *
 * Cada identidade sondada é um job de dezenas de segundos no servidor, então
 * só se sonda **o par que está na tela** e **depois de decidido o encaixe**
 * da comparação: antes disso a identidade que a tela tem é a desalinhada
 * (0, 0, 1), e sondá-la ocuparia uma vaga com um job que ninguém vai olhar.
 * «Decidido» é o efeito do alinhamento automático ter chegado ao fim por
 * qualquer ramo — alinhou, ou pulou (link exportado, link sem auto, perfil
 * sem auto, alinhamento manual prévio) — e não a mera ausência de um encaixe
 * em curso: no primeiro render do par o encaixe ainda não começou e já não
 * está «pendente».
 */
export function maskPollAllowed(input: {
  compareId: string | null | undefined;
  autoAlignDecidedFor: string | null;
  firstAlignPending: boolean;
  onScreen: boolean;
}): boolean {
  return Boolean(input.compareId) && input.onScreen && input.autoAlignDecidedFor === input.compareId && !input.firstAlignPending;
}

/**
 * F-CP.12i (3.ª rodada) — qual compare conta como DECIDIDO depois de uma sessão
 * nova no mesmo par. Trocar camada com o encaixe automático já feito, e «Trocar
 * A e B» (a pose vem decidida), recriam o compare com o encaixe em tela
 * mantido: esse compare nasce decidido, e a sondagem da máscara abre para ele
 * sem passar pelo efeito do encaixe — que o pula, porque já está carimbado.
 * Sem encaixe mantido ninguém decide aqui (`null`): a decisão anterior, de
 * outro compare, não vale para o novo, e o efeito do encaixe é quem o decide.
 */
export function decidedAfterSession(input: {
  keepAlignment: boolean;
  previousDecided: string | null;
  nextCompareId: string;
}): string | null {
  if (input.keepAlignment || input.previousDecided === input.nextCompareId) return input.nextCompareId;
  return null;
}

export interface MaskPollFailure {
  /** Parar de sondar: o estado abaixo vai para a pílula (erro retentável). */
  stop: boolean;
  status: CompareMaskStatus | null;
}

function syntheticError(code: string, message: string): CompareMaskStatus {
  return { state: "error", stage: "raw", error: { code, message, retryable: true }, retryable: true };
}

/**
 * O que fazer quando a própria sondagem falhou (não o job): 4xx que não é 401
 * (sessão em renovação) nem 409 (cache ausente, que o handler global já
 * reenfileira) é definitivo — 400 de pedido inválido, 404 de compare sumido —
 * e vira erro retentável na pílula; o resto (rede, 5xx, 401, 409) sonda de
 * novo até `MASK_POLL_MAX_FAILURES` seguidas.
 */
export function maskPollFailure(error: unknown, consecutiveFailures: number): MaskPollFailure {
  const status = (error as { status?: unknown } | null)?.status;
  const http = typeof status === "number" ? status : null;
  if (http !== null && http >= 400 && http < 500 && http !== 401 && http !== 409) {
    return { stop: true, status: syntheticError(`http_${http}`, `O servidor recusou a sondagem (HTTP ${http}).`) };
  }
  if (consecutiveFailures >= MASK_POLL_MAX_FAILURES) {
    return { stop: true, status: syntheticError("status_unreachable", "O servidor não respondeu à sondagem.") };
  }
  return { stop: false, status: null };
}

/**
 * No A + B os dois pares têm máscara própria e a pílula é uma: mostra o que
 * estiver pendente e, com os dois pendentes, o **mais atrasado** — o que vai
 * ficar pronto por último. Um erro é o mais atrasado de todos (sem o clique
 * não fica pronto nunca); depois o que ainda não respondeu; depois os tiles
 * pela fração feita; o piso e a lista vêm depois dos tiles.
 */
export function pickLaggingMask<T extends { status: CompareMaskStatus | null; pending: boolean }>(a: T, b: T): T {
  if (!a.pending) return b;
  if (!b.pending) return a;
  return maskProgressRank(a.status) <= maskProgressRank(b.status) ? a : b;
}

function maskProgressRank(status: CompareMaskStatus | null): number {
  if (!status) return 0;
  if (status.state === "error") return -1;
  if (status.state === "ready") return 10;
  if (status.stage === "regions") return 3;
  if (status.stage === "floor") return 2;
  const total = Number(status.total ?? 0);
  const done = Math.max(0, Math.min(total, Number(status.done ?? 0)));
  return 1 + (total > 0 ? done / total : 0);
}

export interface CompareMaskCopy {
  title: string;
  detail: string | null;
  tone: "progress" | "error";
  retryable: boolean;
}

/**
 * O que a pílula diz no lugar da camada pintada, com a arte visível por baixo.
 *
 * `n de N tiles` só quando o servidor já contou (`total > 0`); o passo do piso
 * e a lista deferida (páginas acima do teto do backend) têm frase própria,
 * porque não são «tiles». O erro traz o motivo e, quando o servidor diz que
 * vale tentar, o botão. `ready` não tem frase: a pílula não existe com a
 * máscara pronta.
 */
export function maskProgressCopy(status: CompareMaskStatus | null | undefined): CompareMaskCopy {
  if (!status) {
    return { title: "Comparando em alta resolução…", detail: null, tone: "progress", retryable: false };
  }
  if (status.state === "error") {
    const mensagem = status.error?.message?.trim() || null;
    const codigo = status.error?.code ?? "";
    const detail =
      codigo === "mask_too_large"
        ? "A página é grande demais para a comparação em alta resolução neste servidor."
        : codigo === "classes_unavailable"
          ? "Um dos PDFs não pôde ser lido agora. Tente de novo em instantes."
          : codigo === "queue_unavailable"
            ? "A fila de comparação não aceitou o pedido. Tente de novo em instantes."
            : mensagem;
    return {
      title: "Não foi possível comparar em alta resolução",
      detail,
      tone: "error",
      retryable: status.retryable ?? status.error?.retryable ?? false,
    };
  }
  if (status.stage === "floor") {
    return { title: "Aplicando a área mínima…", detail: null, tone: "progress", retryable: false };
  }
  if (status.stage === "regions") {
    return { title: "Identificando as diferenças…", detail: null, tone: "progress", retryable: false };
  }
  const total = Number(status.total ?? 0);
  const done = Math.max(0, Math.min(total, Number(status.done ?? 0)));
  const detail = total > 0 ? `${done} de ${total} tiles` : null;
  return { title: "Comparando em alta resolução…", detail, tone: "progress", retryable: false };
}

/** A resposta 202 de preview, tiles ou lista, como erro tipado para quem pediu esperar. */
export class CompareMaskPendingError extends Error {
  readonly status: CompareMaskStatus;

  constructor(status: CompareMaskStatus) {
    super(`compare mask ${status.state}`);
    this.name = "CompareMaskPendingError";
    this.status = status;
  }
}

export function isCompareMaskPending(error: unknown): error is CompareMaskPendingError {
  return error instanceof CompareMaskPendingError || (error as { name?: string } | null)?.name === "CompareMaskPendingError";
}

/**
 * Um 202 nunca é «ok» (revisão da 12i, F2): tratado como imagem, o JSON do
 * estado virava blob, a decodificação falhava e a tela pintava o véu
 * vermelho. Aqui ele vira `CompareMaskPendingError` com o estado — e quem
 * pediu a imagem devolve o par a «pendente», a pílula volta e a sondagem
 * recomeça. Acontece de verdade DEPOIS de `ready`: o piso de uma Área mínima
 * nova numa página acima do teto do backend (202 `stage: floor`), um tile
 * bruto que sumiu (identidade refeita), um heartbeat velho (redisparo).
 */
export async function throwIfMaskPending(response: { status: number; json(): Promise<unknown> }): Promise<void> {
  if (response.status !== 202) return;
  let status: CompareMaskStatus;
  try {
    status = (await response.json()) as CompareMaskStatus;
  } catch {
    status = { state: "pending", stage: "raw" };
  }
  throw new CompareMaskPendingError(status);
}
